import { Directive, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { RBCheck } from '@lfw/roesberg';
import { merge, Subscription } from 'rxjs';
import { delay, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { AppConfig } from '../../../appconfig';
import { ProjectsService } from '../../../projects/projects.service';
import { LfwRouterService } from '../../../route/lfw-router.service';
import { MenuCommand } from '../../../shared/models/menu-command';
import { InfoService } from '../../../shared/services/info.service';
import { SettingsService } from '../../../shared/settings/settings.service';
import { HttpLoadingService } from '../../http-loading.service';
import { MenuService } from '../../menu.service';
import { UserInformationService } from '../../user-information.service';

@Directive()
export class NavigationBaseComponent implements OnInit, OnDestroy {
  protected menuService = inject(MenuService);
  protected projectsService = inject(ProjectsService);
  protected infoService = inject(InfoService);
  protected httpLoadingService = inject(HttpLoadingService);
  protected settingsService = inject(SettingsService);
  protected userInformationService = inject(UserInformationService);
  protected routerService = inject(LfwRouterService);
  protected config = inject(AppConfig);
  protected ngZone = inject(NgZone);
  private readonly router = inject(Router);

  subscription: Subscription = new Subscription();
  _infoText = '';
  loading$ = this.httpLoadingService.loading$.pipe(delay(1));

  ngOnInit(): void {
    // Trigger menu rebuild when necessary and schedule emission outside Angular's zone.
    this.subscription.add(
      merge(
        this.projectsService.moreThanOneProjectAvailable$,
        this.httpLoadingService.loading$.pipe(
          distinctUntilChanged(),
          filter((loading) => !loading), // only react when HTTP settles (loading becomes false)
          delay(100), // Give change detection time to complete
          map(() => null)
        )
      ).subscribe(() => {
        this.ngZone.runOutsideAngular(() => {
          queueMicrotask(() => this.menuService.rebuilt$.next());
        });
      })
    );

    this.subscription.add(
      this.infoService.statusUpdated.subscribe((text) => {
        setTimeout(() => (this._infoText = text));
      })
    );

    // The status text belongs to the page that set it, so it is dropped on every navigation.
    // Pages showing a list re-publish their count afterwards; NavigationEnd fires before the
    // activated component's ngOnInit, so their value survives (see nav-status-order.spec.ts).
    // Routed through InfoService rather than assigning _infoText, to keep the setTimeout ordering.
    this.subscription.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => this.infoService.clearStatus())
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  get isProjectAvailable(): boolean {
    return this.projectsService.isProjectSelected;
  }

  get projectName(): string {
    return this.projectsService.currentProjectName;
  }

  get infoText(): string {
    return this._infoText;
  }

  get userName(): string {
    const userInfo = this.userInformationService.userInformation;

    if (!userInfo.isEmpty) {
      return `${userInfo.displayName}`;
    }
    return '-';
  }

  get hasProjectName(): boolean {
    return !RBCheck.isNullOrEmpty(this.projectsService.currentProjectName);
  }

  get hasInfoText(): boolean {
    return !RBCheck.isNullOrEmpty(this._infoText);
  }

  get longVersion(): string {
    return 'Version ' + this.config.displayVersion();
  }
  get shortVersion(): string {
    return 'V' + this.config.displayVersion();
  }

  get searchButtonVisible(): boolean {
    return false;
  }

  userCommands$ = this.menuService.rebuilt$.asObservable().pipe(
    map(() => {
      const cmds = [this.menuService.cmdSettings];
      if (this.menuService.cmdDefaultHelp) {
        cmds.push(this.menuService.cmdDefaultHelp);
      }
      if (this.menuService.cmdCustomHelp) {
        cmds.push(this.menuService.cmdCustomHelp);
      }
      if (this.menuService.cmdAbout) {
        cmds.push(this.menuService.cmdAbout);
      }
      cmds.push(this.menuService.cmdSignOff);
      return cmds.filter((command): command is MenuCommand => !!command);
    })
  );

  get infoCommands(): MenuCommand[] {
    return [];
  }

  availableItems$ = merge(this.projectsService.moreThanOneProjectAvailable$, this.menuService.rebuilt$).pipe(
    map(() => {
      const allCommands: MenuCommand[] = [
        this.menuService.cmdOrders,
        this.menuService.cmdTemporaryOfflineOrders,
        this.menuService.cmdOfflineOrders
      ].filter((command): command is MenuCommand => !!command);

      return [
        this.menuService.cmdProject,
        ...allCommands.filter((item) => !item.needProject || this.projectsService.isProjectSelected)
      ].filter((command): command is MenuCommand => !!command);
    })
  );

  showToolTip(): boolean {
    return this.settingsService.settings.viewMode.showTooltips;
  }

  get isMenuVisible(): boolean {
    // Only compute visibility - no side effects!
    return (
      !this.settingsService.settings.viewMode.isFullScreen &&
      !this.settingsService.runningInWindowMode() &&
      this.menuService.navigationAllowed
    );
  }
}
